import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { clearContentCache } from "@/lib/useContent";
import { formatApiError } from "@/context/AuthContext";
import {
    Save, RotateCcw, Plus, Trash2, Edit3, Check, X,
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon, Upload, Navigation, UserCheck, Radio, CalendarClock, Ban, MessageSquareQuote, Star, Mic, Play,
} from "lucide-react";
import { Field } from "../shared.jsx";

export default function BrandingEditor() {
    const [logo, setLogo] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () =>
        api.get("/content/site_logo")
            .then((r) => setLogo(r.data?.data?.data_url ? r.data.data : null))
            .catch(() => setLogo(null));

    useEffect(() => { load(); }, []);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 1_500_000) {
            toast.error("Logo must be 1.5 MB or smaller.");
            return;
        }
        const form = new FormData();
        form.append("file", file);
        setBusy(true);
        try {
            await api.post("/admin/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
            clearContentCache();
            toast.success("Logo uploaded.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Remove the logo? The default brand text will be shown again.")) return;
        setBusy(true);
        try {
            await api.delete("/admin/logo");
            clearContentCache();
            setLogo(null);
            toast.success("Logo removed.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card p-8 max-w-3xl" data-testid="branding-editor">
            <h2 className="font-serif text-2xl mb-2">Header logo</h2>
            <p className="text-[var(--sb-text-dim)] text-sm mb-6">
                Upload a PNG, JPEG, SVG, WEBP or GIF. Max 1.5 MB. For best results use a transparent PNG or SVG sized around 400×120 pixels.
            </p>

            <div className="mb-6 p-6 bg-[var(--sb-bg)] border border-[var(--sb-border)] flex items-center justify-center min-h-[140px]">
                {logo?.data_url ? (
                    <img src={logo.data_url} alt="Logo" className="max-h-24 max-w-full object-contain" data-testid="branding-preview" />
                ) : (
                    <div className="text-[var(--sb-text-muted)] text-sm uppercase tracking-wider">No logo set</div>
                )}
            </div>

            {logo && (
                <div className="text-xs text-[var(--sb-text-muted)] mb-6">
                    {logo.filename} · {Math.round((logo.size || 0) / 1024)} KB · {logo.mime_type}
                </div>
            )}

            <div className="flex gap-3 flex-wrap">
                <label className="btn-primary text-sm cursor-pointer" style={{padding: "0.55rem 1.1rem"}} data-testid="branding-upload-label">
                    <Upload size={14} /> {logo ? "Replace logo" : "Upload logo"}
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif" onChange={onFile} className="hidden" disabled={busy} data-testid="branding-upload-input" />
                </label>
                {logo && (
                    <button onClick={remove} disabled={busy} className="btn-outline text-sm" style={{padding: "0.55rem 1.1rem"}} data-testid="branding-remove">
                        <Trash2 size={14} /> Remove
                    </button>
                )}
            </div>

            {busy && <p className="text-sm text-[var(--sb-text-dim)] mt-4">Working…</p>}
        </div>
    );
}



/* -------------------------------------------------------------------------- */
/* Flight Path editor — the 'Survivor' map pins                               */
